import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, CalendarX, Trash2, XCircle } from "lucide-react";
import clsx from "clsx";
import { Contracts, type ContractStatus } from "@/api/contracts";
import { apiErrorMessage } from "@/api/client";
import { confirmDialog, toast } from "@/lib/feedback";
import { ENDED, KIND_LABEL, PROGRESS, STATUS, statusColor } from "@/lib/contracts";
import { eur } from "@/lib/format";
import { useContractSave } from "@/lib/useContractSave";
import { useUIStore } from "@/store/ui";
import { OverflowMenu, Segmented } from "@/components/ui";
import { StatusPill, WaitingPill } from "@/components/contracts/ContractBits";
import ContractThread from "@/components/contracts/ContractThread";
import {
  DocumentPanel,
  KeyDatesPanel,
  TermsPanel,
  VersionViewer,
} from "@/components/contracts/ContractPanels";

type Pane = "thread" | "document" | "terms";

/** One contract: its progress, the exchange thread, the document and its terms. */
export default function ContractDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const openDeal = useUIStore((s) => s.openDeal);
  const [pane, setPane] = useState<Pane>("thread");
  const [viewing, setViewing] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["contract", id],
    queryFn: () => Contracts.get(id),
    retry: false,
  });
  const save = useContractSave(id);
  const c = query.data;

  if (query.isLoading) return <div className="p-8 text-mut-500">Loading…</div>;
  if (!c) {
    return (
      <div className="p-8">
        <p className="text-md font-medium">
          This contract doesn't exist, or you don't have access to it.
        </p>
        <Link to="/contracts" className="btn mt-4">
          Back to contracts
        </Link>
      </div>
    );
  }

  const setStatus = (s: ContractStatus) => s !== c.status && save.mutate({ status: s });
  const remove = async () => {
    if (
      !(await confirmDialog({
        title: `Delete ${c.title}?`,
        body: "This removes the contract, every version and its whole thread. It can't be undone.",
        confirmLabel: "Delete contract",
        danger: true,
      }))
    )
      return;
    try {
      await Contracts.remove(c.id);
      void qc.invalidateQueries({ queryKey: ["contracts"] });
      toast.ok("Contract deleted");
      navigate("/contracts");
    } catch (e) {
      toast.err(apiErrorMessage(e));
    }
  };
  const viewed = c.versions.find((v) => v.id === viewing);

  return (
    <div className="flex h-[calc(100dvh-3rem)] flex-col md:h-screen">
      <header className="space-y-3 border-b border-fg/[0.07] px-4 pb-4 pt-4 md:px-6 md:pt-5">
        <div className="flex items-start gap-3">
          <button
            className="btn btn-ghost !p-2"
            aria-label="Back"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/contracts"))}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold tracking-tight md:text-xl">{c.title}</h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-mut-500">
              <span>{KIND_LABEL[c.kind]}</span>
              {c.account_id && c.account_name && (
                <>
                  <span>·</span>
                  <Link to={`/accounts/${c.account_id}`} className="hover:text-accent-azure">
                    {c.account_name}
                  </Link>
                </>
              )}
              {c.deal_id && c.deal_lead && (
                <>
                  <span>·</span>
                  <button className="hover:text-accent-azure" onClick={() => openDeal(c.deal_id!)}>
                    Deal: {c.deal_lead}
                  </button>
                </>
              )}
              {c.value_eur != null && (
                <>
                  <span>·</span>
                  <span className="font-medium tabular-nums text-mut-100">{eur(c.value_eur)}</span>
                </>
              )}
              <WaitingPill contract={c} />
            </div>
          </div>
          <OverflowMenu
            label="Contract actions"
            items={[
              ...(c.status === "signed"
                ? [
                    {
                      label: "Mark expired",
                      icon: <CalendarX className="h-4 w-4" />,
                      onClick: () => setStatus("expired"),
                    },
                    {
                      label: "Mark terminated",
                      icon: <XCircle className="h-4 w-4" />,
                      onClick: () => setStatus("terminated"),
                    },
                  ]
                : []),
              ...(!ENDED.includes(c.status) && c.status !== "signed"
                ? [
                    {
                      label: "Cancel (won't go ahead)",
                      icon: <Ban className="h-4 w-4" />,
                      onClick: () => setStatus("cancelled"),
                    },
                  ]
                : []),
              ...(c.can_delete
                ? [
                    {
                      label: "Delete contract",
                      icon: <Trash2 className="h-4 w-4" />,
                      danger: true,
                      onClick: () => void remove(),
                    },
                  ]
                : []),
            ]}
          />
        </div>

        <StatusStepper status={c.status} disabled={save.isPending} onPick={setStatus} />
      </header>

      <div className="border-b border-fg/[0.07] px-4 py-2 lg:hidden">
        <Segmented<Pane>
          label="Contract section"
          value={pane}
          onChange={setPane}
          options={[
            { value: "thread", label: "Thread" },
            { value: "document", label: "Document & dates" },
            { value: "terms", label: "Terms" },
          ]}
        />
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className={clsx("min-h-0 min-w-0", pane !== "thread" && "hidden lg:block")}>
          <ContractThread contract={c} onView={setViewing} />
        </div>
        <aside
          className={clsx(
            "min-h-0 space-y-6 overflow-y-auto border-fg/[0.07] bg-ink-800/50 px-4 py-4 lg:border-l lg:px-5",
            pane === "thread" && "hidden lg:block",
          )}
        >
          <div className={clsx("space-y-6", pane === "terms" && "hidden lg:block")}>
            <DocumentPanel contract={c} onView={setViewing} />
            <KeyDatesPanel contract={c} />
          </div>
          <div
            className={clsx(
              pane === "document" && "hidden lg:block",
              "lg:border-t lg:border-fg/[0.07] lg:pt-5",
            )}
          >
            <TermsPanel contract={c} />
          </div>
        </aside>
      </div>

      {viewed && (
        <VersionViewer contractId={c.id} version={viewed} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

/** The road to signature. Click a step to move the contract there. On phones it
 *  scrolls sideways and keeps the current step in view. */
function StatusStepper({
  status,
  disabled,
  onPick,
}: {
  status: ContractStatus;
  disabled: boolean;
  onPick: (s: ContractStatus) => void;
}) {
  const currentRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [status]);
  const current = PROGRESS.indexOf(status);
  return (
    <div
      className="-mx-4 flex items-center gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:flex-wrap md:px-0"
      role="radiogroup"
      aria-label="Status"
    >
      {PROGRESS.map((s, i) => {
        const isCurrent = s === status;
        const isPast = current >= 0 && i < current;
        return (
          <button
            key={s}
            ref={isCurrent ? currentRef : undefined}
            role="radio"
            aria-checked={isCurrent}
            disabled={disabled}
            onClick={() => onPick(s)}
            className={clsx(
              "shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-sm font-medium transition-[filter]",
              !isCurrent && !isPast && "bg-fg/[0.05] text-mut-500 hover:brightness-95",
            )}
            style={
              isCurrent
                ? { background: statusColor(s), color: "#fff" }
                : isPast
                  ? { background: statusColor(s, 0.12), color: statusColor(s) }
                  : undefined
            }
          >
            {STATUS[s].label}
          </button>
        );
      })}
      {ENDED.includes(status) && <StatusPill status={status} />}
    </div>
  );
}
